import { useState } from 'react';
import clsx from '../utils/clsx';
import common from '../styles/common.module.css';
import styles from '../styles/Battlefield.module.css';
import NumberInput from './NumberInput';
import { EditorToggle, NameColourFields, OptionRow } from './battlefieldBuild/editorParts';

const TRANSPARENCY = [
  { value: 0, label: 'Opaque', hint: 'Blocks sight. Cover by how much of the target it hides.' },
  {
    value: 1,
    label: 'Translucent',
    hint: 'You can see through it but not aim through it: full cover whatever its height. Smoke, frosted glass.',
  },
  { value: 2, label: 'Transparent', hint: 'You see straight through it. It still stops the bullet.' },
];

/**
 * WHAT YOU CAN WALK THROUGH - a different question from what you can see
 * through, and deliberately independent of it. Smoke you see nothing
 * through and walk straight into; glass is the other way round.
 */
const PERMEABILITY = [
  { value: 0, label: 'Solid', hint: 'A wall. Stone, steel, wood, glass.' },
  {
    value: 1,
    label: 'Dense',
    hint: 'You get through, at double cost - the difficult-going rule. Forest, thicket, deep water.',
  },
  { value: 2, label: 'Open', hint: 'No cost at all to cross. Smoke, light, a force curtain.' },
];

/** Name, colour and hardness - the hardness IS the cover AP, clamped to 0-10. */
function MaterialFields({ material, set }) {
  return (
    <div className={styles.buildRow}>
      <NameColourFields
        what="Material"
        name={material.name}
        colour={material.colour}
        onName={(name) => set({ name })}
        onColour={(colour) => set({ colour })}
      />
      <label className={styles.buildField}>
        Hardness (AP)
        <NumberInput
          className={common.input}
          aria-label="Hardness"
          min={0}
          max={10}
          value={material.hardness}
          onChange={(value) => set({ hardness: Math.max(0, Math.min(10, value ?? 0)) })}
        />
      </label>
    </div>
  );
}

/** GM only, the note about cover, and the new and delete buttons. */
function MaterialFooter({ material, set, canDelete, onAdd, onDelete }) {
  return (
    <div className={styles.buildRow}>
      <label className={clsx(common.label, common.checkRow)} style={{ marginBottom: 0 }}>
        <input
          type="checkbox"
          aria-label="GM only"
          data-testid="material-hidden"
          checked={Boolean(material.hidden)}
          onChange={(e) => set({ hidden: e.target.checked })}
        />
        GM only
      </label>
      <span className={styles.buildHint}>
        Cover level comes from how tall you build and who is being shot at - nothing to set here.
      </span>
      <div className={styles.spacer} />
      <button type="button" className={common.btnSmall} data-testid="material-add" onClick={onAdd}>
        + New material
      </button>
      <button
        type="button"
        className={clsx(common.btnSmall, styles.dangerBtn)}
        data-testid="material-delete"
        disabled={!canDelete}
        onClick={onDelete}
        title="Anything already built from it keeps its properties"
      >
        Delete
      </button>
    </div>
  );
}

/**
 * MaterialEditor - three dials and a name.
 *
 * Materials are DATA, not a fixed list. Hardness becomes the cover AP,
 * transparency decides SIGHT and permeability decides PASSAGE - three
 * independent properties, because stuff really is like that: smoke
 * blinds you and lets you through, glass does the opposite, and a force
 * curtain is hard, clear and open all at once.
 *
 * Cover LEVEL is still derived from how tall you built and who is being
 * shot at, so there is nothing else here to fill in.
 */
export default function MaterialEditor({ materials, selected, onChange, onAdd, onDelete }) {
  const [open, setOpen] = useState(false);
  const material = materials[selected];
  if (!material) return null;
  const set = (fields) => onChange({ ...material, ...fields });
  return (
    <div className={styles.materialEditor} data-testid="material-editor">
      <EditorToggle open={open} setOpen={setOpen} testId="material-editor-toggle">
        ✎ Edit material
      </EditorToggle>
      {open && (
        <div className={styles.materialPanel}>
          <MaterialFields material={material} set={set} />
          <OptionRow
            label="Transparency"
            options={TRANSPARENCY}
            value={material.transparency}
            testPrefix="transparency-"
            onPick={(transparency) => set({ transparency })}
          />
          <OptionRow
            label="Permeability"
            options={PERMEABILITY}
            value={material.permeability ?? 0}
            testPrefix="permeability-"
            onPick={(permeability) => set({ permeability })}
          />
          <MaterialFooter
            material={material}
            set={set}
            canDelete={Object.keys(materials).length > 1}
            onAdd={onAdd}
            onDelete={() => onDelete(selected)}
          />
        </div>
      )}
    </div>
  );
}
